<template>
    <form class="h-full w-full flex flex-col p-8" @submit.prevent="submitForm">
        <h1 class="text-2xl font-bold underline">Basic Information</h1>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
            <div class="flex flex-col" :class="{ invalid: !name.isValid }">
                <label for="name">Name*</label>
                <input
                    type="text"
                    id="name"
                    v-model.trim="name.val"
                    placeholder=""
                    @blur="clearValidity('name')"
                />
                <transition name="fade" mode="out-in">
                    <p v-if="!name.isValid" class="h-4 text-xs text-red-600">
                        {{ name.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col" :class="{ invalid: !industry.isValid }">
                <label for="industry">Industry*</label>
                <input
                    type="text"
                    id="industry"
                    v-model.trim="industry.val"
                    placeholder=""
                    @blur="clearValidity('industry')"
                />
                <transition name="fade" mode="out-in">
                    <p v-if="!industry.isValid" class="h-4 text-xs text-red-600">
                        {{ industry.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col" :class="{ invalid: !participants.isValid }">
                <label for="participants">Participants*</label>
                <input
                    type="text"
                    id="participants"
                    v-model.trim="participants.val"
                    placeholder=""
                    @blur="clearValidity('participants')"
                />
                <transition name="fade" mode="out-in">
                    <p v-if="!participants.isValid" class="h-4 text-xs text-red-600">
                        {{ participants.msg }}
                    </p>
                </transition>
            </div>
        </div>
        <div class="flex flex-col mt-6" :class="{ invalid: !description.isValid }">
            <label for="description">Description*</label>
            <div class="mt-2">
                <ckeditor :editor="editor" v-model="description.val" :config="editorConfig"></ckeditor>
            </div>
            <transition name="form-fade" mode="out-in">
                <p v-if="!description.isValid" class="h-4 text-xs text-red-600 ml-1">
                    {{ description.msg }}
                </p>
            </transition>
        </div>

        <h1 class="text-2xl font-bold underline mt-6">Upload Images</h1>
        <div v-for="(item, index) in inputAttachments" :key="index" class="flex justify-start items-center mt-4">
            <div class="w-8 flex-shrink-0">{{ index + 1 }}</div>
            <div class="flex-grow grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div class="flex flex-col">
                    <div class="flex h-full w-full">
                        <div v-if="!item.is_new" class="flex flex-col">
                            <div class="h-5"></div>
                            <div class="flex-auto flex justify-center ">
                                <div class="flex justify-start items-center">
                                    <a :href="getSrcFile(item.path)" target="_blank">
                                        <img class="h-12 w-12 border object-contain" :src="getSrcFile(item.path)" alt="upload_file">
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div v-else class="flex flex-col">
                            <label :for="`document-file-${index+1}`">Upload<span class="text-xs text-secondary ml-1">(JPG, JPEG, and PNG max 2MB)</span></label>
                            <input :id="`document-file-${index}`" :key="`document-file-${index}`" type="file" @change="handleFileChange" accept="image/*" />
                            <transition name="fade" mode="out-in">
                                <p v-if="!item.document.isValid" class="h-4 text-xs text-red-600">
                                    {{ item.document.msg }}
                                </p>
                            </transition>
                        </div>
                    </div>
                </div>
            </div>
            <div class="w-20 flex-shrink-0 flex justify-start items-center ml-6">
                <div v-if="showAddMoreAttachmentBtn(index)" class="flex flex-col mr-2">
                    <div class="h-5"></div>
                    <div class="w-full flex justify-center">
                        <div @click.stop="addMoreAttachment" class="flex items-center bg-ternary border border-ternary hover:bg-white px-2 py-1 rounded-lg cursor-pointer text-white hover:text-ternary">
                            <icon-plus class="w-4 h-4"></icon-plus>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col">
                    <div class="h-5"></div>
                    <div v-if="inputAttachments.length > 1" @click.stop="removeAttachment(index)" class="border border-red-600 bg-red-600 hover:bg-white px-2 py-1 rounded-lg cursor-pointer group">
                        <icon-minus class="w-4 h-4 text-white group-hover:text-red-600"></icon-minus>
                    </div>
                </div>
            </div>
        </div>
        <transition name="form-fade" mode="out-in">
            <p v-if="attachmentErrorMsg" class="h-4 text-xs text-red-600 mt-1 text-center">
                {{ attachmentErrorMsg }}
            </p>
        </transition>

        <div class="flex-auto w-full flex flex-col justify-end items-center mt-8 pb-8">
            <div v-if="isLoading" class="w-36 border-2 font-medium border-ternary text-ternary bg-white rounded-lg px-4 py-2 cursor-pointer">
                <base-spinner-with-text class-list="h-6 w-6 text-ternary capitalize" :text="getLoadingBtnText" text-class-list="text-ternary"></base-spinner-with-text>
            </div>
            <button v-else class="w-36 border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-4 py-2 uppercase text-sm cursor-pointer">{{ getBtnText }}</button>
        </div>
    </form>
    <div class="h-full flex justify-center items-center" v-if="isLoading">
        <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import IconPlus from "../../components/icons/IconPlus.vue";
import IconMinus from "../../components/icons/IconMinus.vue";
import CKEditor from '@ckeditor/ckeditor5-vue';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
export default {
    name: "project-form",
    emits: ["created", "updated", "close"],
    props: ["mode", "selectedItem"],
    components: {
        IconPlus,
        IconMinus,
        ckeditor: CKEditor.component,
    },
    data() {
        return {
            name: {
                val: "",
                isValid: true,
                msg: "",
            },
            industry: {
                val: "",
                isValid: true,
                msg: "",
            },
            participants: {
                val: "",
                isValid: true,
                msg: "",
            },
            description: {
                val: "",
                isValid: true,
                msg: "",
            },
            editor: ClassicEditor,
            editorConfig: {
            },
            inputAttachments: [],
            attachmentIndex: 0,
            attachmentErrorMsg: "",
            deletedAttachmentIds: [],
            formIsValid: true,
            isLoading: false,
            selectedEntities: [],
        };
    },
    created() {
        if (this.mode == 'edit') {
            this.setFormData();
        } else {
            this.initializeVariables();
        }
    },
    computed: {
        getBtnText() {
            if (this.mode == 'create') {
                return "Create";
            } else {
                return "Update";
            }
        },
        getLoadingBtnText() {
            if (this.mode == 'create') {
                return "Creating...";
            } else {
                return "Updating...";
            }
        },
    },
    methods: {
        getSrcFile(url) {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        allowNumbersOnly(event) {
            const charCode = event.which || event.keyCode;
            if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                event.preventDefault();
            }
        },
        clearValidity(input) {
            this[input].isValid = true;
            this[input].msg = "";
        },
        validateAttachment() {
            this.attachmentErrorMsg = "";
            if (this.mode == 'create') {
                const noFileUploaded = this.inputAttachments.some((obj) => obj.document.val === null);
                if (!noFileUploaded) {
                    return true;
                } else {
                    return false;
                }
            } else {
                const newAttachments = this.inputAttachments.filter(attachment => attachment.is_new);
                newAttachments.forEach(attachment => {
                    if (attachment.document.val === null) {
                        return false;
                    }
                });
                if (this.inputAttachments[this.attachmentIndex].is_new) {
                    if (this.inputAttachments[this.attachmentIndex].document.val == null) {
                        return false;
                    }
                }
                return true;
            }
        },
        validateAttachmentInEditMode() {
            this.attachmentErrorMsg = "";
            const newAttachments = this.inputAttachments.filter(attachment => attachment.is_new);
            newAttachments.forEach(attachment => {
                if (attachment.document.val === null) {
                    attachment.document.isValid = false;
                    attachment.document.msg = "Document is required";
                    this.formIsValid = false;
                } else {
                    attachment.document.isValid = true;
                    attachment.document.msg = "";
                }
            });
            this.inputAttachments.forEach(attachment => {

                if (attachment.is_new) {
                    if (attachment.document.val == null) {
                        this.attachmentErrorMsg = "Please fill all the details.";
                        this.formIsValid = false;
                    }
                }
                
            });
        },
        addMoreAttachment() {
            const isValid = this.validateAttachment();
            if (isValid) {
                const obj = {
                    is_new: true,
                    id: null,
                    path: "",
                    document: {
                        val: null,
                        isValid: true,
                        msg: "",
                    }
                }
                this.inputAttachments.push(obj);
                this.attachmentIndex = this.attachmentIndex + 1;
            } else {
                this.attachmentErrorMsg = "You must upload a file.";
            }
        },
        removeAttachment(index) {
            if (this.mode == 'edit') {
                this.deletedAttachmentIds.push(parseInt(this.inputAttachments[index].id));
            }
            this.inputAttachments.splice(index, 1);
            this.attachmentIndex = this.attachmentIndex - 1;
        },
        removeAttachedFile(index) {
            this.deletedAttachmentIds.push(parseInt(this.inputAttachments[index].id));
            this.inputAttachments[index].id = null;
            this.inputAttachments[index].is_new = true;
        },
        showAddMoreAttachmentBtn(index) {
            if (this.inputAttachments.length <= 1) {
                return true;
            } else {
                if ((this.inputAttachments.length - 1) == index) {
                    return true;
                } else {
                    return false;
                }
            }  
        },
        validateForm() {
            this.formIsValid = true;
            if (this.name.val === "") {
                this.name.isValid = false;
                this.name.msg = "Name must not be empty.";
                this.formIsValid = false;
            }
            if (this.industry.val === "") {
                this.industry.isValid = false;
                this.industry.msg = "Industry must not be empty.";
                this.formIsValid = false;
            }
            if (this.participants.val === "") {
                this.participants.isValid = false;
                this.participants.msg = "Participants must not be empty.";
                this.formIsValid = false;
            }
            if (this.description.val === "") {
                this.description.isValid = false;
                this.description.msg = "Description must not be empty.";
                this.formIsValid = false;
            }

            if (this.mode == 'edit') {
                this.validateAttachmentInEditMode();
            }
        },
        async submitForm() {
            this.validateForm();

            if (!this.formIsValid) {
                return;
            }

            let modifiedInputAttachments = this.inputAttachments.filter(item => item.document.val != null).map(item => {
                return {
                    id: parseInt(item.id),
                    document: item.document.val
                };
            });

            const formData = {
                name: this.name.val,
                industry: this.industry.val,
                participants: this.participants.val,
                description: this.description.val,
                input_attachments: modifiedInputAttachments,
                deleted_attachment_ids: this.deletedAttachmentIds,
            };

            const CREATE_MUTATION = gql`
                mutation CreateProject($input: ProjectInput!) {
                    createProject(input: $input) {
                        error
                        message
                    }
                }
            `;

            const UPDATE_MUTATION = gql`
                mutation UpdateProject($token: String!, $input: ProjectInput!) {
                    updateProject(token: $token, input: $input) {
                        error
                        message
                    }
                }
            `;

            this.isLoading = true;
            let error_msg = null;
            let success_msg = null;

            try {
                await this.$apollo.mutate({
                    mutation: this.mode == 'create' ? CREATE_MUTATION : UPDATE_MUTATION,
                    variables: {
                        token: this.mode == 'edit' ? this.selectedItem.token : null,
                        input: formData
                    },
                    context: {
                        headers: {
                            'content-type': 'multipart/form-data',
                        },
                    },
                }).then((data) => {
                    if (this.mode == 'create') {
                        if (data.data.createProject.error) {
                            error_msg = data.data.createProject.message;
                        } else {
                            success_msg = data.data.createProject.message;
                        }
                    } else {
                        if (data.data.updateProject.error) {
                            error_msg = data.data.updateProject.message;
                        } else {
                            success_msg = data.data.updateProject.message;
                        }
                    }
                }).catch((error) => {
                    error_msg = error;
                })

            } catch (error) {
                error_msg = error.message || "Failed to authenticate, try later.";
            }

            if (error_msg === null) {
                if (this.mode == 'create') {
                    this.$emit('created', success_msg);
                } else {
                    this.$emit('updated', success_msg);
                }
            } else {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: error_msg,
                }).then((result) => {
                    if (result.value || result.dismiss) {
                        this.$emit('close');
                    }
                })
            }

            this.isLoading = false;

        },
        initializeVariables() {
            const obj = {
                is_new: true,
                id: null,
                path: "",
                document: {
                    is_new: true,
                    val: null,
                    isValid: true,
                    msg: "",
                }
            }
            this.inputAttachments.push(obj);
        },
        setFormData() {
            this.name.val = this.selectedItem.name;
            this.industry.val = this.selectedItem.industry;
            this.participants.val = this.selectedItem.participants;
            this.description.val = this.selectedItem.description;
            if (this.selectedItem.attachments.length > 0) {
                this.attachmentIndex = this.selectedItem.attachments.length - 1;
                this.selectedItem.attachments.forEach(cp => {
                    const obj = {
                        is_new: false,
                        id: cp.id,
                        path: cp.path,
                        document: {
                            val: null,
                            isValid: true,
                            msg: "",
                        }
                    }
                    this.inputAttachments.push(obj);
                });
            } else {
                const obj = {
                    is_new: true,
                    id: null,
                    path: "",
                    document: {
                        val: null,
                        isValid: true,
                        msg: "",
                    }
                }
                this.inputAttachments.push(obj);
            }
        },
        onPickFile(ref) {
            this.$refs[ref].click();
        },
        handleFileChange(event) {

            this.inputAttachments[this.attachmentIndex].document.isValid = true;
            this.inputAttachments[this.attachmentIndex].document.msg = "";
            const file = event.target.files[0];

            if (file) {
                if (!file.type.includes('image/')) {
                    this.inputAttachments[this.attachmentIndex].document.isValid = false;
                    this.inputAttachments[this.attachmentIndex].document.msg = 'Only JPG, JPEG and PNG files are allowed.';
                } else if (file.size > 2 * 1024 * 1024) {
                    this.inputAttachments[this.attachmentIndex].document.isValid = false;
                    this.inputAttachments[this.attachmentIndex].document.msg = 'File size must be less than 2MB.';
                } else {
                    this.inputAttachments[this.attachmentIndex].document.isValid = true;
                    this.inputAttachments[this.attachmentIndex].document.msg = '';
                    this.inputAttachments[this.attachmentIndex].document.val = file;
                    this.inputAttachments[this.attachmentIndex].document.is_new = true;
                    this.inputAttachments[this.attachmentIndex].document.id = null;
                    this.inputAttachments[this.attachmentIndex].document.path = "";
                }
            }

        },
    },
}
</script>

<style lang="scss" scoped>
label {
  @apply mb-1 block text-sm;
}
input[type="text"],
input[type="email"],
input[type="number"],
input[type="password"],
input[type="date"],
select {
  @apply block w-full border rounded-lg py-2 px-2;
}
input[type="text"]:focus,
input[type="email"]:focus,
input[type="number"]:focus,
input[type="password"]:focus,
input[type="date"]:focus,
select:focus {
  outline: none;
  border-color: #394867;
  box-shadow: 0 0 0 2px #394867;
}
.invalid label {
  @apply text-red-600;
}
.invalid input,
.invalid select {
  border: 1px solid red;
}
input[type="checkbox"] {
  @apply w-auto;
}
.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  height: 0px;
}
.ht-42 {
    height: 42px;
}
.form-fade-enter-active,
.form-fade-leave-active {
  transition: all 0.5s ease;
}

.form-fade-enter-from,
.form-fade-leave-to {
  opacity: 0;
  height: 0px;
}
</style>

<style lang="scss">
.ck-content {
    height: 400px;
}
.ck-powered-by {
    display: none !important;
}
.mx-input {
    height: 42px !important;
}
.mx-datepicker {
    width: 100% !important;
}
.ck.ck-editor__editable_inline {
    padding: 0px 20px;
}
</style>